// GUARDA DA SESSÃO — a ligação com o navegador (07/10/2026). A régua (quando
// renovar, quando é seguro repetir) mora em guardaSessao.ts, testada à parte.
//
// Instalada UMA vez em src/index.tsx, logo depois do Amplify.configure. Cobre
// as chamadas à API feitas por axios (interceptor na instância global — o
// portal não usa axios.create) e por fetch (embrulho de window.fetch só para a
// URL da API: o Amplify fala com o Cognito por fetch e isso não é tocado).
// Não importa antd: o aviso de "sessão expirou" é desenhado por quem se
// registrar em aoSessaoExpirar (App.tsx, ShellAutenticado).
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import {
  CHECAR_A_CADA_MS, LIMITE_RENOVACAO_MS, authorizationDe, comAuthorization, classificarFalha,
  ehDaApi, msDesdeEnvioSeguro, podeForcarRenovacao, podeRepetir, precisaRenovar, renovacaoRecusada,
} from './guardaSessao';

type Resultado = { token: string; recusada: boolean };

const relogioCorrido = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());
let agora = relogioCorrido;

let instalada = false;
let ultimaRenovacao = agora();
let ultimaForcada = -Infinity;          // quando a guarda forçou a última renovação
let ultimoTempoEsgotado = -Infinity;    // quando uma renovação ficou sem resposta
let emVoo: Promise<Resultado> | null = null;
let avisada = false;
// Há alguém logado? (na tela de login não há o que renovar — e "recusada" ali
// não é sessão que expirou)
let temSessao = false;
const ouvintes: Array<() => void> = [];

/** Quem desenha o aviso "sua sessão expirou" se registra aqui; devolve o cancelamento. */
export function aoSessaoExpirar(fn: () => void): () => void {
  ouvintes.push(fn);
  return () => { const i = ouvintes.indexOf(fn); if (i >= 0) ouvintes.splice(i, 1); };
}

function avisarSessaoExpirada(motivo: string) {
  console.warn('[sessão] expirou:', motivo);
  // sem quem desenhe o aviso (página pública, tela de login): não mexe na página
  if (avisada || !ouvintes.length) return;
  avisada = true;
  ouvintes.forEach(fn => { try { fn(); } catch { /* segue */ } });
}

/** Renova JÁ (forceRefresh), com limite de tempo e sem pedidos simultâneos duplicados.
 *  Logo depois de outra renovação forçada (ou de uma sem resposta) não força de novo:
 *  devolve o token guardado — é o freio do laço numa pane da API. */
export function renovarSessao(): Promise<Resultado> {
  if (emVoo) return emVoo;
  if (!podeForcarRenovacao(agora() - ultimaForcada, agora() - ultimoTempoEsgotado)) {
    return fetchAuthSession().then((s: any) => ({ token: s?.tokens?.idToken?.toString() || '', recusada: false }))
      .catch(() => ({ token: '', recusada: false }));
  }
  ultimaForcada = agora();
  emVoo = (async (): Promise<Resultado> => {
    try {
      const sessao: any = await Promise.race([
        fetchAuthSession({ forceRefresh: true }),
        new Promise((_, rej) => setTimeout(() => rej(Object.assign(new Error('renovação sem resposta'), { name: 'TempoEsgotado' })), LIMITE_RENOVACAO_MS)),
      ]);
      const token = sessao?.tokens?.idToken?.toString() || '';
      if (token) { ultimaRenovacao = agora(); return { token, recusada: false }; }
      // sem token depois de forçar = o Amplify apagou a sessão (Cognito recusou)
      return { token: '', recusada: true };
    } catch (e: any) {
      if (e?.name === 'TempoEsgotado') ultimoTempoEsgotado = agora();
      return { token: '', recusada: renovacaoRecusada(e?.name) };
    } finally {
      // libera depois que todos os que esperavam leram o resultado
      setTimeout(() => { emVoo = null; }, 0);
    }
  })();
  return emVoo;
}

async function checarPorTempo() {
  if (!temSessao || !precisaRenovar(agora() - ultimaRenovacao)) return;
  const r = await renovarSessao();
  if (r.recusada) avisarSessaoExpirada('renovação periódica recusada');
}

/** Falhou uma chamada à API: renova e diz se dá para repetir (e com qual token). */
async function aposFalha(o: { metodo: string; status?: number; temResposta: boolean; codigo?: string; tokenEnviado: string; enviadoEm: number; enviadoEmParede: number }) {
  const tipo = classificarFalha({ status: o.status, temResposta: o.temResposta, codigo: o.codigo });
  if (tipo === 'outra' || !o.tokenEnviado) return null;
  // o token guardado já é outro (alguém renovou depois que esta chamada saiu)? usa ele
  let r: Resultado | null = null;
  try {
    const guardado = (await fetchAuthSession() as any)?.tokens?.idToken?.toString() || '';
    if (guardado && guardado !== o.tokenEnviado) r = { token: guardado, recusada: false };
  } catch { /* segue para a renovação forçada */ }
  if (!r) r = await renovarSessao();
  if (r.recusada) { avisarSessaoExpirada(`renovação recusada depois de ${o.temResposta ? o.status : 'falha sem resposta'}`); return null; }
  temSessao = true;
  if (!r.token) return null;   // rede de verdade: deixa a tela mostrar o erro dela
  const ok = podeRepetir({ metodo: o.metodo, status: o.status, temResposta: o.temResposta,
    tokenEnviado: o.tokenEnviado, tokenNovo: r.token,
    msDesdeEnvio: msDesdeEnvioSeguro(agora() - o.enviadoEm, Date.now() - o.enviadoEmParede) });
  return ok ? r.token : null;
}

export function instalarGuardaSessao(apiUrl: string, opcoes: { agora?: () => number } = {}) {
  if (instalada || typeof window === 'undefined') return;
  instalada = true;
  if (opcoes.agora) agora = opcoes.agora;   // só o teste troca o relógio corrido
  ultimaRenovacao = agora();

  // renovações feitas pelo próprio Amplify também contam; a recusa vira aviso
  Hub.listen('auth', ({ payload }: any) => {
    if (payload?.event === 'tokenRefresh' || payload?.event === 'signedIn') { ultimaRenovacao = agora(); avisada = false; temSessao = true; }
    if (payload?.event === 'signedOut') temSessao = false;
    if (payload?.event === 'tokenRefresh_failure' && temSessao && renovacaoRecusada(payload?.data?.error?.name)) {
      avisarSessaoExpirada('o Amplify não conseguiu renovar (Cognito recusou)');
    }
  });

  // 1) por tempo corrido — não confia no relógio do computador. Ao abrir o
  // portal, se há sessão, renova logo: a idade do token guardado só se mede pelo
  // relógio do computador, e é nele que não dá para confiar.
  fetchAuthSession().then((s: any) => {
    if (!s?.tokens?.idToken) return;
    temSessao = true;
    setTimeout(() => { renovarSessao().then(r => { if (r.recusada) avisarSessaoExpirada('renovação ao abrir recusada'); }); }, 5000);
  }).catch(() => { /* sem sessão ou sem rede: o intervalo e as chamadas cuidam */ });
  setInterval(() => { checarPorTempo(); }, CHECAR_A_CADA_MS);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') checarPorTempo(); });
  window.addEventListener('online', () => { checarPorTempo(); });

  // 2) axios (instância global)
  axios.interceptors.request.use((cfg: any) => { cfg.__sessaoEnviadoEm = agora(); cfg.__sessaoEnviadoEmParede = Date.now(); return cfg; });
  axios.interceptors.response.use(undefined, async (err: any) => {
    const cfg = err?.config;
    if (!cfg || cfg.__sessaoRepetida || axios.isCancel?.(err)) throw err;
    const url = axios.getUri ? axios.getUri(cfg) : String(cfg.url || '');
    if (!ehDaApi(url, apiUrl) && !ehDaApi(String(cfg.url || ''), apiUrl)) throw err;
    const token = await aposFalha({ metodo: String(cfg.method || 'get'), status: err?.response?.status,
      temResposta: !!err?.response, codigo: err?.code, tokenEnviado: authorizationDe(cfg.headers),
      enviadoEm: cfg.__sessaoEnviadoEm ?? agora(), enviadoEmParede: cfg.__sessaoEnviadoEmParede ?? Date.now() });
    if (!token) throw err;
    cfg.__sessaoRepetida = true;
    cfg.headers = comAuthorization(cfg.headers, token);
    return axios.request(cfg);
  });

  // 3) fetch (só a URL da API)
  if (typeof window.fetch !== 'function') return;
  const fetchOriginal = window.fetch.bind(window);
  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
    if (!ehDaApi(url, apiUrl)) return fetchOriginal(input as any, init);
    const enviadoEm = agora();
    const enviadoEmParede = Date.now();
    const tokenEnviado = authorizationDe(init?.headers) || (typeof input === 'object' && 'headers' in (input as any) ? authorizationDe((input as Request).headers) : '');
    const metodo = String(init?.method || (typeof input === 'object' && 'method' in (input as any) ? (input as Request).method : 'GET'));
    // só dá para repetir se o corpo puder ser mandado de novo (string, FormData, nada)
    const corpo = init?.body;
    const repetivel = typeof input === 'string' || input instanceof URL
      ? (corpo == null || typeof corpo === 'string' || (typeof FormData !== 'undefined' && corpo instanceof FormData) || (typeof URLSearchParams !== 'undefined' && corpo instanceof URLSearchParams))
      : false;
    let resp: Response | null = null;
    let erroRede: any = null;
    try { resp = await fetchOriginal(input as any, init); } catch (e) { erroRede = e; }
    if (resp && resp.status !== 401) return resp;
    const token = await aposFalha({ metodo, status: resp?.status, temResposta: !!resp, codigo: erroRede?.name,
      tokenEnviado, enviadoEm, enviadoEmParede });
    if (token && repetivel) {
      return fetchOriginal(input as any, { ...(init || {}), headers: comAuthorization(init?.headers, token) });
    }
    if (resp) return resp;
    throw erroRede;
  }) as typeof window.fetch;
}

/** Só para teste: volta ao estado inicial. */
export function _reiniciarParaTeste() {
  instalada = false; agora = relogioCorrido; ultimaRenovacao = agora(); ultimaForcada = -Infinity; ultimoTempoEsgotado = -Infinity;
  emVoo = null; avisada = false; temSessao = false; ouvintes.length = 0;
}
